import { isRetryableError } from './get-status-code'

type Process<T> = (retry: (sleep?: number) => Promise<T>, count: number) => Promise<T>

const SLEEP_DURATIONS = [5 * 1000, 5 * 1000, 10 * 1000, 15 * 1000, 30 * 1000, 60 * 1000]

class Worker<T> {
  private tryCount = 0

  constructor(private readonly process: Process<T>) {
  }

  get sleepDuration(): number {
    const index = Math.min(this.tryCount - 1, SLEEP_DURATIONS.length - 1)
    return SLEEP_DURATIONS[index]
  }

  next(): Promise<T> {
    this.tryCount++
    return this.process((duration) => {
      return sleep(duration || this.sleepDuration).then(() => {
        return this.next()
      })
    }, this.tryCount)
  }
}

export default function retryPromise<T = void>(process: Process<T>): Promise<T> {
  return new Worker<T>(process).next()
}

function sleep(millis: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    setTimeout(resolve, millis)
  })
}

export type RetryHandler = <T>(reason: any, retry: (sleep?: number) => Promise<T>, count: number) => Promise<T>

/**
 * 必要に応じてリトライを実行する関数を返す。
 * @param {number} maxCount - 最大実行回数
 * @return {RetryHandler<T>} - リトライを実行する関数
 */
export function createRetryHandler(maxCount: number = 3): RetryHandler {
  return <T>(reason: any, retry: (sleep?: number) => Promise<T>, count: number) => {
    if (isRetryableError(reason)) {
      if (count < maxCount) {
        return retry()
      }
    }
    console.error(reason)
    return Promise.reject(reason)
  }
}
